import notes from '@/sui/data/sheets/edit/notes'
import tools from '@/sui/data/sheets/edit/tools'
import patient from '@/sui/data/sheets/edit/patient'
import bounds from '@/sui/data/sheets/edit/bounds'
import micro from '@/sui/data/microOrgs'
import clone from 'clone'

import { goFieldsToJS, goTreeToJS } from '@/sui/data/sheets/edit/fromGo'

import assert from '@/sui/tools/assert'
import ice from '@/sui/tools/ice'
import date from '@/sui/tools/date'
import go from '@/gojs/go'

const fields = (function () {
  const target = clone(go.badicause.v2026.fields)
  const moFields = target.microOrganisms.cfg.fields
  moFields.code.cfg.values = micro.list.v2

  const result = goFieldsToJS(
    target, ['unitType', 'sheetNumber']
  )
  return result
})()

fields.sheetNumber = tools.opts(
  patient.sheetNumber.v2,
  { srv: go.badicause.v2026.fields.sheetNumber.sfield }
)

fields.participants.config.display = {
  layout: {
    xs: 1, sm: 3
  }
}

fields.bmrBhreMicroOrgs.config.display = {
  layout: {
    xs: 1, sm: 2, md: 3
  }
}

fields.proximalManipulationOperators.config.display = {
  layout: {
    xs: 1, sm: 3
  }
}

fields.ctrMissingProtocolsList.config.display = {
  layout: {
    xs: 1, sm: 2
  }
}

fields.periMissingProtocolsList.config.display = {
  layout: {
    xs: 1, sm: 2
  }
}

fields.ctrMissingTrainingCourseList.config.display = {
  layout: {
    xs: 1, sm: 2
  }
}

fields.periMissingTrainingCourseList.config.display = {
  layout: {
    xs: 1, sm: 2
  }
}

fields.ctrMissingPracticeEvaluationList.config.display = {
  layout: {
    xs: 1, sm: 2
  }
}

fields.periMissingPracticeEvaluationList.config.display = {
  layout: {
    xs: 1, sm: 2
  }
}

fields.unitType = tools.opts(
  patient.unitType.v2.rw, {
    srv: go.badicause.v2026.fields.unitType.sfield,
    sp: 'unitType_v2'
  }
)

tools.addMultiline([
  fields.insertionIncidentDetail,
  fields.periDistalManipulationIncidentDetail,
  fields.periPrincipalLineChangeIncidentDetail,
  fields.proximalManipulationIncidentDetail,
  fields.needleInsertionIncidentDetail,
  fields.dressingRepairIncidentDetail,
  fields.ctrDistalManipulationIncidentDetail,
  fields.ctrPrincipalLineChangeIncidentDetail,

  fields.securityFenceList.fields.description,
  fields.securityFenceList.fields.notEffectiveDetail,

  fields.identifiedCauseList.fields.description,
  fields.identifiedCauseList.fields.proposedCourseOfAction,
  fields.identifiedCauseList.fields.planning
])

function fillInsertionSignsDelayIFP (vm, target) {
  const insertion = target[fields.insertionDate.field]
  const signs = target[fields.generalSignsDate.field]
  if (insertion == null || signs == null) {
    return
  }

  const delay = date.getElapsed(signs, insertion, 'days')
  vm.$set(target, fields.insertionSignsDelay.field, delay < 0 ? null : delay)
}

fields.insertionDate.commit = function (vm, e, target) {
  vm.$set(target, fields.insertionDate.field, e)
  fillInsertionSignsDelayIFP(vm, target)
}

fields.localSignsDate.config.optional = true

fields.generalSignsDate.commit = function (vm, e, target) {
  vm.$set(target, fields.generalSignsDate.field, e)
  fillInsertionSignsDelayIFP(vm, target)
}

fields.insertionSignsDelay.readOnly = true
fields.insertionSignsDelay.config.unit = 'jours'

const moFields = fields.microOrganisms.fields
moFields.code.convert.special = 'microOrgs'

function checkDates (result, sheet, year) {
  assert.notNull(result)
  assert.notNull(sheet)
  assert.isNumber(year)

  const dates = notes.collectDates(
    result, sheet, [
      fields.analysisDate,
      fields.birthdate,
      fields.entry,
      fields.episod,
      fields.insertionDate,
      fields.localSignsDate,
      fields.generalSignsDate,
      fields.removalDate,
      fields.deathDate
    ]
  )

  const oldest = notes.oldest[`${year}`]
  const newest = notes.newest[`${year}`]
  const start = notes.start[`${year}`]
  const now = notes.now()

  const comparisons = [
    { f: start, s: dates.analysisDate },
    { f: dates.analysisDate, s: now },
    { f: dates.analysisDate, s: newest },

    { f: oldest, s: dates.birthdate },
    { f: dates.birthdate, s: dates.entry },
    { f: dates.birthdate, s: dates.episod },
    { f: dates.birthdate, s: dates.insertionDate },
    { f: dates.birthdate, s: dates.localSignsDate },
    { f: dates.birthdate, s: dates.generalSignsDate },
    { f: dates.birthdate, s: dates.removalDate },
    { f: dates.birthdate, s: dates.deathDate },

    { f: dates.birthdate, s: dates.analysisDate },
    { f: dates.entry, s: dates.analysisDate },
    { f: dates.episod, s: dates.analysisDate },
    { f: dates.localSignsDate, s: dates.analysisDate },
    { f: dates.generalSignsDate, s: dates.analysisDate },
    { f: dates.insertionDate, s: dates.analysisDate },

    { f: dates.insertionDate, s: dates.localSignsDate },
    { f: dates.insertionDate, s: dates.generalSignsDate },
    { f: dates.insertionDate, s: dates.removalDate },
    { f: dates.insertionDate, s: dates.deathDate },

    { f: dates.insertionDate, s: dates.episod },
    { f: dates.entry, s: dates.episod },

    { f: dates.removalDate, s: dates.deathDate }
  ]

  return notes.compareDates(comparisons)
}

function checkUf (result, sheet, year) {
  assert.notNull(result)
  assert.notNull(sheet)
  assert.isNumber(year)

  const uf = sheet[fields.uf.field]
  const ufNote = result[fields.uf.field]
  assert.notNull(ufNote)

  if (uf == null ||
      (uf.length >= 3 && uf.length <= 50) ||
      ufNote.type != null) {
    return
  }

  ufNote.type = 'error'
  ufNote.tooltip =
      `Le nom de l'UF doit contenir entre 3 et 50 caractères.`
}

function checkBactMo (result, sheet, year) {
  assert.notNull(result)
  assert.notNull(sheet)
  assert.isNumber(year)

  const microOrgs = sheet[fields.microOrganisms.field]
  assert(microOrgs.length !== 0)

  if (microOrgs.length !== 1) {
    return
  }

  assert(microOrgs.length === 1)
  const mo = microOrgs[0]
  const hasMO = mo.code !== undefined
  if (hasMO) {
    return
  }

  const note = result[fields.episod.field]
  note.type = 'error'
  note.tooltip =
      `Une bactériémie doit contenir au moins un micro-organisme.`
}

function getPatientDaysAge (result, sheet) {
  const dates = notes.collectDates(
    result, sheet, [
      fields.birthdate,
      fields.entry
    ]
  )
  const bd = dates.birthdate.date
  const ed = dates.entry.date

  if (bd == null || ed == null) {
    return -1
  }

  return date.getElapsed(
    date.tostr(ed),
    date.tostr(bd),
    'days'
  )
}

function checkIsNewBorn (result, sheet, year) {
  assert.notNull(result)
  assert.notNull(sheet)
  assert.isNumber(year)

  const newBornF = fields.isNewborn.field
  const isNewBorn = sheet[newBornF]
  const daysAge = getPatientDaysAge(result, sheet)

  if (isNewBorn == null ||
      isNewBorn === 2 ||
      daysAge < 0 ||
      (daysAge >= 0 && daysAge < 28)) {
    return
  }

  const newBornNote = result[newBornF]
  const birthdateNote = result[fields.birthdate.field]
  const entryNote = result[fields.entry.field]

  entryNote.type = birthdateNote.type = newBornNote.type = 'warning'
  entryNote.tooltip = birthdateNote.tooltip = newBornNote.tooltip =
      `Incohérence possible : le patient n'est pas un nouveau né lors de son entrée dans le service.`
}

function checkIsAdultFromRea (result, sheet, year) {
  assert.notNull(result)
  assert.notNull(sheet)
  assert.isNumber(year)

  const reaAdultF = fields.isAdultFromReanimation.field
  const isReaAdu = sheet[reaAdultF]
  const daysAge = getPatientDaysAge(result, sheet)

  if (isReaAdu == null ||
      isReaAdu === 2 ||
      daysAge < 0 ||
      daysAge > 6575) { // 18 years
    return
  }

  const reaAdultNote = result[reaAdultF]
  const birthdateNote = result[fields.birthdate.field]
  const entryNote = result[fields.entry.field]

  reaAdultNote.type = birthdateNote.type = entryNote.type = 'warning'
  reaAdultNote.tooltip = birthdateNote.tooltip = entryNote.tooltip =
      `Incohérence possible : le patient n'est pas adulte lors de son entrée dans le service.`
}

function checkUnitType (
  result, sheet, year, field, regExp, text
) {
  assert.notNull(result)
  assert.notNull(sheet)
  assert.notNull(field)
  assert.notNull(regExp)
  assert.notNull(text)
  assert.isNumber(year)

  const utF = fields.unitType.field
  const ut = sheet[utF]
  const checkField = sheet[field]

  if (ut == null ||
      checkField == null ||
      checkField === 2 ||
      regExp.test(ut)) {
    return
  }

  const alertFields = [utF, field]
  for (let i = 0; i < alertFields.length; i++) {
    const f = alertFields[i]
    const note = result[f]
    note.type = 'warning'
    note.tooltip =
      `Incohérence possible : le code du service semble incohérent avec un patient ${text}.`
  }
}

export function checkDeviceInsertionSite (result, sheet, year) {
  assert.notNull(result)
  assert.notNull(sheet)
  assert.isNumber(year)

  const ktField = fields.catheterType.field
  const ctrInsertionSiteField = fields.ctrInsertionSite.field

  const kt = sheet[ktField]
  const ctrInsertionSite = sheet[ctrInsertionSiteField]

  if (kt == null || ctrInsertionSite == null) {
    return
  }

  const controls = [
    {
      device: 'C4',
      sites: [7, 10],
      tooltip: `les PICCs sont généralement insérés dans un membre supérieur`
    },
    {
      device: 'C5',
      sites: [10, 12],
      tooltip: `les cathéters artériels sont généralement insérés en radial`
    },
    {
      device: 'C8',
      sites: [7, 10],
      tooltip: `les MID line avec prolongateur sont généralement insérés dans un membre supérieur`
    }
  ]

  for (let i = 0; i !== controls.length; ++i) {
    const c = controls[i]
    if (kt === c.device && c.sites.indexOf(ctrInsertionSite) === -1) {
      const ktNote = result[ktField]
      const insertionSiteNote = result[ctrInsertionSiteField]

      ktNote.type = insertionSiteNote.type = 'warning'
      ktNote.tooltip = insertionSiteNote.tooltip =
          `Incohérence possible : ${c.tooltip}`
      break
    }
  }
}

function check9IntFields (result, sheet, alreadyCheckedList) {
  const fieldsKeys = Object.keys(fields)
  for (let i = 0; i < fieldsKeys.length; i++) {
    const key = fieldsKeys[i]
    const field = fields[key]
    if (field.type === 'numeric' && !field.readOnly) {
      const notChecked =
          alreadyCheckedList.findIndex(d => d.field === field.field) === -1
      if (notChecked) {
        const f = field.field
        const n = sheet[f]
        if (n == null) {
          continue
        }

        const intN = parseInt(n)
        const digits = field.config.digits
        const targetsNum = []
        let unkNum = ''
        for (let j = 1; j <= digits; j++) {
          unkNum = `${unkNum}9`
          if (targetsNum.length !== digits - 1) {
            targetsNum.push(parseInt(unkNum))
          }
        }

        if (targetsNum.find(d => d === intN) != null) {
          const note = result[f]
          note.type = 'warning'
          note.tooltip =
              `Incohérence possible : indiquez vous une valeur non connue ? Si oui, indiquez ${unkNum}.`
        }
      }
    }
  }
}

function checkNumericValues (result, sheet, year) {
  assert.notNull(result)
  assert.notNull(sheet)
  assert.isNumber(year)

  const numericChecks = [
    bounds.bmi,
    bounds.birthWeight,
    bounds.gestationalAge,
    bounds.igsII
  ]
  notes.checkNumerics(result, sheet, numericChecks)
  check9IntFields(result, sheet, numericChecks)
}

function buildNotes2026 (result, sheet, surveillance) {
  checkUf(result, sheet, 2026)
  checkDates(result, sheet, 2026)
  checkBactMo(result, sheet, 2026)
  checkIsNewBorn(result, sheet, 2026)
  checkIsAdultFromRea(result, sheet, 2026)
  checkNumericValues(result, sheet, 2026)
  checkDeviceInsertionSite(result, sheet, 2026)

  checkUnitType(
    result,
    sheet,
    2026,
    fields.isNewborn.field,
    /NEO/,
    'nouveau né'
  )
  checkUnitType(
    result,
    sheet,
    2026,
    fields.isAdultFromReanimation.field,
    /AD REA/,
    'adulte en réanimation'
  )
  return result
}

const tree = goTreeToJS(
  go.badicause.v2026.tree, fields
)

const titles = {
  administrative: {
    text: `1. PARTIE ADMINISTRATIVE`,
    start: fields.sheetNumber.field,
    type: 'title-alt'
  },

  meeting: {
    text: `LA RÉUNION D'ANALYSE DES CAUSES`,
    start: fields.analysisDate.field
  },

  team: {
    text: `COMPOSITION DE L'ÉQUIPE`,
    start: fields.participants.field
  },

  clinical: {
    text: `2. PARTIE CLINIQUE`,
    start: fields.birthdate.field,
    type: 'title-alt'
  },

  patient: {
    text: `LE PATIENT`,
    start: fields.birthdate.field
  },

  neo: {
    text: `POUR LES NOUVEAU-NÉS`,
    start: fields.birthWeight.field,
    type: 'subtitle-alt',
    hiddenWhenFieldIgnored: true
  },

  adRea: {
    text: `POUR LES PATIENTS EN SERVICE DE RÉANIMATION ADULTE`,
    start: fields.iGSII.field,
    type: 'subtitle-alt',
    hiddenWhenFieldIgnored: true
  },

  visit: {
    text: `LE SÉJOUR DU PATIENT`,
    start: fields.unitType.field
  },

  bacteremia: {
    text: `LA BACTÉRIÉMIE`,
    start: fields.episod.field
  },

  chrono: {
    text: `3. CHRONOLOGIE DE L'ÉVÉNEMENT`,
    start: fields.insertionDate.field,
    type: 'title-alt'
  },

  insertion: {
    text: `POSE DU CATHÉTER IMPLIQUÉ DANS LA BACTÉRIÉMIE`,
    start: fields.insertionDate.field
  },

  proxi: {
    text: `MANIPULATIONS PROXIMALES`,
    start: fields.proximalManipulation.field,
    hiddenWhenFieldIgnored: true
  },

  dressing: {
    text: `RÉFECTION DU PANSEMENT`,
    start: fields.dressingRepair.field,
    hiddenWhenFieldIgnored: true
  },

  needle: {
    text: `POSE D'AIGUILLE SUR CCI`,
    start: fields.needleInsertion.field,
    hiddenWhenFieldIgnored: true
  },

  line: {
    text: `CHANGEMENT DE LIGNE PRINCIPALE`,
    start: fields.principalLineChanges.field,
    hiddenWhenFieldIgnored: true
  },

  research: {
    text: `4. RECHERCHE DES CAUSES IMMÉDIATES ET PROFONDES`,
    start: fields.bacteremiaRiskFactor.field,
    type: 'title-alt'
  },

  immediate: {
    text: `CAUSES IMMÉDIATES`,
    start: fields.bacteremiaRiskFactor.field,
    type: 'subtitle-alt'
  },

  causePatient: {
    text: `LE PATIENT`,
    start: fields.bacteremiaRiskFactor.field
  },

  causeCatheter: {
    text: `LE CATHÉTER`,
    start: fields.insertionIncident.field
  },

  causeCatheterInsertion: {
    text: `LA POSE DU CATHÉTER`,
    start: fields.insertionIncident.field,
    type: 'subtitle-sub'
  },

  periCauseInsertionOutfit: {
    text: `TENUES DE L'OPÉRATEUR ET DU PATIENT`,
    start: fields.periInsertionOperatorOutfitNotCompliant.field,
    type: 'observation',
    hiddenWhenFieldIgnored: true
  },

  periCauseInsertionHandHygiene: {
    text: `HYGIÈNE DES MAINS DE L'OPÉRATEUR`,
    start: fields.periInsertionOperatorHandHygieneNotCompliant.field,
    type: 'observation',
    hiddenWhenFieldIgnored: true
  },

  periCauseInsertionSkinPreparation: {
    text: `PRÉPARATION CUTANÉE DU SITE D'INSERTION`,
    start: fields.periInsertionSkinScrutinyNotCompliant.field,
    type: 'observation',
    hiddenWhenFieldIgnored: true
  },

  periCauseInsertionUltrasound: {
    text: `POSE DU CATHÉTER AVEC ÉCHOGUIDAGE`,
    start: fields.periInsertionWithUltrasound.field,
    type: 'observation',
    hiddenWhenFieldIgnored: true
  },

  periCauseDistalManipulation: {
    text: `MANIPULATIONS DISTALES`,
    start: fields.periDistalManipulationIncident.field,
    type: 'subtitle-sub',
    hiddenWhenFieldIgnored: true
  },

  periCauseLine: {
    text: `CHANGEMENT DE LIGNES PRINCIPALES`,
    start: fields.periPrincipalLineChangeIncident.field,
    type: 'subtitle-sub',
    hiddenWhenFieldIgnored: true
  },

  periCauseRinsing: {
    text: `RINÇAGE PULSÉ`,
    start: fields.periRinsingNotCompliant.field,
    type: 'observation',
    hiddenWhenFieldIgnored: true
  },

  ctrCauseInsertionOutfit: {
    text: `TENUES DE L'OPÉRATEUR ET DU PATIENT`,
    start: fields.ctrInsertionOperatorOutfitNotCompliant.field,
    type: 'observation',
    hiddenWhenFieldIgnored: true
  },

  ctrCauseInsertionHandHygiene: {
    text: `HYGIÈNE DES MAINS DE L'OPÉRATEUR`,
    start: fields.ctrInsertionOperatorHandHygieneNotCompliant.field,
    type: 'observation',
    hiddenWhenFieldIgnored: true
  },

  ctrCauseInsertionSkinPreparation: {
    text: `PRÉPARATION CUTANÉE DU SITE D'INSERTION`,
    start: fields.ctrInsertionSkinScrutinyNotCompliant.field,
    type: 'observation',
    hiddenWhenFieldIgnored: true
  },

  ctrCauseInsertionUltrasound: {
    text: `POSE DU CATHÉTER AVEC ÉCHOGUIDAGE`,
    start: fields.ctrInsertionWithUltrasound.field,
    type: 'observation',
    hiddenWhenFieldIgnored: true
  },

  ctrCauseProximalManipulation: {
    text: `MANIPULATIONS PROXIMALES`,
    start: fields.proximalManipulationIncident.field,
    type: 'subtitle-sub',
    hiddenWhenFieldIgnored: true
  },

  ctrCauseProximalManipulationOutfit: {
    text: `TENUES DE L'OPÉRATEUR ET DU PATIENT`,
    start: fields.proximalManipulationOperatorOutfitNotCompliant.field,
    type: 'observation',
    hiddenWhenFieldIgnored: true
  },

  ctrCauseProximalManipulationHandHygiene: {
    text: `HYGIÈNE DES MAINS`,
    start: fields.proximalManipulationOperatorHandHygieneNotCompliant.field,
    type: 'observation',
    hiddenWhenFieldIgnored: true
  },

  ctrCauseRinsing: {
    text: `RINÇAGE PULSÉ`,
    start: fields.ctrProximalManipRinsingNotCompliant.field,
    type: 'observation',
    hiddenWhenFieldIgnored: true
  },

  ctrCauseNeedle: {
    text: `POSE D'AIGUILLE SUR CCI`,
    start: fields.needleInsertionIncident.field,
    type: 'subtitle-sub',
    hiddenWhenFieldIgnored: true
  },

  ctrCauseNeedleOutfit: {
    text: `TENUES DE L'OPÉRATEUR ET DU PATIENT`,
    start: fields.needleInsertionOperatorOutfitNotCompliant.field,
    type: 'observation',
    hiddenWhenFieldIgnored: true
  },

  ctrCauseNeedleHandHygiene: {
    text: `HYGIÈNE DES MAINS`,
    start: fields.needleInsertionOperatorHandHygieneNotCompliant.field,
    type: 'observation',
    hiddenWhenFieldIgnored: true
  },

  ctrCauseNeedleSkinPreparation: {
    text: `PRÉPARATION CUTANÉE`,
    start: fields.needleInsertionSkinScrutinyNotCompliant.field,
    type: 'observation',
    hiddenWhenFieldIgnored: true
  },

  ctrCauseNeedleRinsing: {
    text: `RINÇAGE PULSÉ`,
    start: fields.ctrCCIManipRinsingNotCompliant.field,
    type: 'observation',
    hiddenWhenFieldIgnored: true
  },

  ctrCauseDressing: {
    text: `RÉFECTION DU PANSEMENT`,
    start: fields.dressingRepairIncident.field,
    type: 'subtitle-sub',
    hiddenWhenFieldIgnored: true
  },

  ctrCauseDressingOutfit: {
    text: `TENUES DE L'OPÉRATEUR ET DU PATIENT`,
    start: fields.dressingRepairOperatorOutfitNotCompliant.field,
    type: 'observation',
    hiddenWhenFieldIgnored: true
  },

  ctrCauseDressingHandHygiene: {
    text: `HYGIÈNE DES MAINS`,
    start: fields.dressingRepairOperatorHandHygieneNotCompliant.field,
    type: 'observation',
    hiddenWhenFieldIgnored: true
  },

  ctrCauseDistal: {
    text: `MANIPULATION DISTALE`,
    start: fields.ctrDistalManipulationIncident.field,
    type: 'subtitle-sub',
    hiddenWhenFieldIgnored: true
  },

  ctrCauseLine: {
    text: `CHANGEMENT DE LIGNES PRINCIPALES`,
    start: fields.ctrPrincipalLineChangeIncident.field,
    type: 'subtitle-sub',
    hiddenWhenFieldIgnored: true
  },

  causeSurveillance: {
    text: `SURVEILLANCE DU CATHÉTER`,
    start: fields.dailySurveillanceNotDone.field,
    type: 'subtitle-sub'
  },

  causeCatheterUse: {
    text: `UTILISATION DU CATHÉTER`,
    start: fields.periExpectedTreatmentDurationNotCompliant.field,
    type: 'subtitle-sub'
  },

  deep: {
    text: 'CAUSES PROFONDES',
    start: fields.patientNotInvolvedInPrevention.field,
    type: 'subtitle-alt'
  },

  deepPatient: {
    text: `LIÉES AU PATIENT ET À SA MALADIE`,
    start: fields.patientNotInvolvedInPrevention.field
  },

  deepTask: {
    text: `LIÉES AUX TÂCHES`,
    start: fields.upToDateProtocolsNotAvailable.field
  },

  deepProfessional: {
    text: `LIÉES AUX SOIGNANTS`,
    start: fields.noRecentTrainingCourse.field
  },

  deepCareTeam: {
    text: `LIÉES À L'ÉQUIPE SOIGNANTE`,
    start: fields.protocolNotUsed.field
  },

  deepEnvironment: {
    text: `LIÉES À L'ENVIRONNEMENT DE TRAVAIL`,
    start: fields.noSuitableRooms.field
  },

  deepSystem: {
    text: `LIÉES AU SYSTÈME (ORGANISATION, MANAGEMENT)`,
    start: fields.flawsInHumanResourcesManagment.field
  },

  fence: {
    text: `BARRIÈRES DE SÉCURITÉ`,
    start: fields.securityFenceList.field,
    type: 'title-alt'
  },

  cause: {
    text: `CAUSES IDENTIFIÉES`,
    start: fields.identifiedCauseList.field,
    type: 'title-alt'
  }
}

const sheet = {
  titles: [
    titles.administrative,
    titles.meeting,
    titles.team,
    titles.clinical,
    titles.patient,
    titles.neo,
    titles.adRea,
    titles.visit,
    titles.bacteremia,
    titles.chrono,
    titles.insertion,
    titles.proxi,
    titles.dressing,
    titles.needle,
    titles.line,
    titles.research,
    titles.immediate,
    titles.causePatient,
    titles.causeCatheter,
    titles.causeCatheterInsertion,
    titles.periCauseInsertionOutfit,
    titles.periCauseInsertionHandHygiene,
    titles.periCauseInsertionSkinPreparation,
    titles.periCauseInsertionUltrasound,
    titles.periCauseDistalManipulation,
    titles.periCauseLine,
    titles.periCauseRinsing,
    titles.ctrCauseInsertionOutfit,
    titles.ctrCauseInsertionHandHygiene,
    titles.ctrCauseInsertionSkinPreparation,
    titles.ctrCauseInsertionUltrasound,
    titles.ctrCauseProximalManipulation,
    titles.ctrCauseProximalManipulationOutfit,
    titles.ctrCauseProximalManipulationHandHygiene,
    titles.ctrCauseRinsing,
    titles.ctrCauseNeedle,
    titles.ctrCauseNeedleOutfit,
    titles.ctrCauseNeedleHandHygiene,
    titles.ctrCauseNeedleSkinPreparation,
    titles.ctrCauseNeedleRinsing,
    titles.ctrCauseDressing,
    titles.ctrCauseDressingOutfit,
    titles.ctrCauseDressingHandHygiene,
    titles.ctrCauseDistal,
    titles.ctrCauseLine,
    titles.causeSurveillance,
    titles.causeCatheterUse,
    titles.deep,
    titles.deepPatient,
    titles.deepTask,
    titles.deepProfessional,
    titles.deepCareTeam,
    titles.deepEnvironment,
    titles.deepSystem,
    titles.fence,
    titles.cause
  ],

  repeatableTitles: [
  ],

  fields: tree,
  buildNotes: buildNotes2026
}

const result = {
  fields,
  titles,
  sheet
}

export default ice.freeze(result, 10)
